import {
  AgencyPlanName,
  AgencyStatus,
  AgencyType,
  DamageStatus,
  IAgency,
  IComment,
  IFile,
  IFivGoal,
  IRole,
  IUser,
  PolicyStatus,
  TaskStatus,
  UserSpecialType,
  UserStatus,
} from 'admiral-core';
import 'dayjs/locale/de';
import dayjs from 'dayjs';
import isToday from 'dayjs/plugin/isToday';
import isYesterday from 'dayjs/plugin/isYesterday';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { BASE_STORAGE_URL } from '@/services/AdmiralApi';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import { FileApi } from '@/services/FileApi';

dayjs.extend(dayOfYear);
dayjs.extend(isLeapYear);
dayjs.extend(isToday);
dayjs.extend(isYesterday);

dayjs.extend(quarterOfYear);
export class Util {
  static wait(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /** Entfernt Entity-Referenzen aus Entwurfs-Kommentaren für API-Requests. */
  static sanitizeNewCommentsForApi(comments: IComment[]): IComment[] {
    return comments.map((comment) => ({
      text: comment.text,
      createdAt: comment.createdAt,
      updatedAt: comment.updatedAt,
    })) as IComment[];
  }

  static userInitials(user: IUser) {
    return user.firstname.substring(0, 1) + '' + user.lastname.substring(0, 1);
  }
  static agencyInitials(agency: IAgency) {
    return agency.name.substring(0, 1) + '' + agency.name.substring(0, 1);
  }

  static getProfilePictureUrl(user: IUser) {
    return `${BASE_STORAGE_URL}/images/users/${user.profilePictureId}`;
  }

  static formatDate(date: Date) {
    const dateDayJs = dayjs(date);
    if (dateDayJs.isToday()) {
      return dateDayJs.format('HH:mm');
    } else if (dateDayJs.isYesterday()) {
      return 'gestern,' + dateDayJs.format(' HH:mm');
    } else if (dateDayJs.isBefore(dateDayJs.subtract(7, 'day'))) {
      return dateDayJs.format('ddd');
    } else {
      return dateDayJs.format('DD.MM.YYYY');
    }
  }
  static formatDate2(date: Date) {
    const dateDayJs = dayjs(date);
    return dateDayJs.format('DD.MM.YYYY');
  }

  static formatDateSemiLong(date: Date) {
    const dateDayJs = dayjs(date);
    return dateDayJs.format('DD.MM.YY');
  }

  static formatDateFull(date: Date) {
    const dateDayJs = dayjs(date);
    return dateDayJs.format('DD.MM.YYYY, HH:mm');
  }

  static formatDateSimple(date: Date) {
    const dateDayJs = dayjs(date);
    return dateDayJs.format('DD. MMM');
  }

  static formatPolicyStatus(policyStatus: PolicyStatus) {
    switch (policyStatus) {
      case PolicyStatus.OPEN:
        return 'OFFEN';
      case PolicyStatus.SUBMITTED:
        return 'EINGEREICHT';
      case PolicyStatus.DECLINED:
        return 'ABGESAGT';
      case PolicyStatus.POLICED:
        return 'POLICIERT';
      case PolicyStatus.PLANNED:
        return 'GEPLANT';
      case PolicyStatus.CANCELED:
        return 'STORNIERT';
      // case PolicyStatus.DELETED:
      //   return 'GELÖSCHT';
    }
  }

  static formatPolicyStatusClass(policyStatus: PolicyStatus) {
    switch (policyStatus) {
      case PolicyStatus.OPEN:
        return 'badge-light-secondary';
      case PolicyStatus.SUBMITTED:
        return 'badge-light-warning';
      case PolicyStatus.POLICED:
        return 'badge-light-success';
      case PolicyStatus.DECLINED:
        return 'badge-light-danger';
      case PolicyStatus.PLANNED:
        return 'badge-light-dark';
      case PolicyStatus.CANCELED:
        return 'badge-light-info';
    }
  }

  static formatTaskStatus(taskStatus: TaskStatus) {
    switch (taskStatus) {
      case TaskStatus.COMPLETED:
        return 'Erledigt';
      case TaskStatus.ARCHIVED:
        return 'Archiviert';
      case TaskStatus.TODO:
        return 'Offen';
      case TaskStatus.IN_PROGRESS:
        return 'In Bearbeitung';
      case TaskStatus.IN_WAITING:
        return 'Warten';
      default:
        return String(taskStatus);
    }
  }

  static formatTaskStatusClass(taskStatus: TaskStatus) {
    switch (taskStatus) {
      case TaskStatus.COMPLETED:
        return 'badge-light-success';
      case TaskStatus.ARCHIVED:
        return 'badge-light-dark';
      case TaskStatus.TODO:
        return 'badge-light-secondary';
      case TaskStatus.IN_PROGRESS:
        return 'badge-light-primary';
      case TaskStatus.IN_WAITING:
        return 'badge-light-warning';
      default:
        return 'badge-light-secondary';
    }
  }

  static formatUserStatus(status: UserStatus) {
    switch (status) {
      case UserStatus.ACTIVE:
        return 'Aktiv';
      case UserStatus.INACTIVE:
        return 'Inaktiv';
      case UserStatus.PENDING:
        return 'Warte auf Bestätigung in E-Mail';
      case UserStatus.DELETED:
        return 'Gelöscht';
      case UserStatus.NOT_VISIBLE:
        return 'Nicht sichtbar';
      case UserStatus.DEACTIVATED:
        return 'Deaktiviert';
      default:
        return '-';
    }
  }

  static formatUserStatusDescription(status: UserStatus) {
    switch (status) {
      case UserStatus.SUSPENDED:
        return 'Der Nutzer ist vorrübergehend gesperrt.';
      case UserStatus.ACTIVE:
        return 'Das Nutzerkonto ist aktiv.';
      case UserStatus.PENDING:
        return 'Das Nutzerkonto muss von Nutzer per E-Mail bestätigt werden.';
      case UserStatus.DEACTIVATED:
        return 'Das Nutzerkonto ist deaktiviert';
      case UserStatus.DELETED:
        return 'Das Nutzerkonto ist gelöscht';
      default:
        return '-';
    }
  }

  static formatUserRoleName(role: IRole) {
    switch (role.name) {
      case 'Admin':
        return 'Admin';
      case 'Manager':
        return 'Manager/in';
      case 'Employee':
        return 'Mitarbeiter/in';
      case 'Guest':
        return 'Gast';
      default:
        return '-';
    }
  }

  static formatUserSpecialType(type: UserSpecialType) {
    switch (type) {
      case UserSpecialType.None:
        return 'Standard';
      case UserSpecialType.Kb:
        return 'KB';
      case UserSpecialType.Kbv:
        return 'KBV';
      case UserSpecialType.Office:
        return 'Büro';
      case UserSpecialType.Boss:
        return 'Inhaber/in';
      default:
        return '-';
    }
  }

  static formatAgencyStatus(agencyStatus: AgencyStatus) {
    if (agencyStatus === AgencyStatus.INACTIVE) {
      return 'Inaktiv';
    } else if (agencyStatus === AgencyStatus.YEARLY) {
      return 'Jährlich';
    } else if (agencyStatus === AgencyStatus.MONTHLY) {
      return 'Monatlich';
    } else if (agencyStatus === AgencyStatus.DEMO) {
      return 'Demo';
    } else if (agencyStatus === AgencyStatus.PERPETUAL) {
      return 'Unbefristet';
    } else if (agencyStatus === AgencyStatus.CANCELLED) {
      return 'Gekündigt';
    }
  }

  static formatAgencyType(agencyType: AgencyType) {
    if (agencyType === AgencyType.ALLIANZ) {
      return 'Allianz Agentur';
    } else if (agencyType === AgencyType.GENERIC) {
      return 'Sonstige Agentur';
    } else if (agencyType === AgencyType.HDI) {
      return 'HDI Agentur';
    }
  }

  static formatAgencyPlan(agencyPlanName: AgencyPlanName) {
    if (agencyPlanName === AgencyPlanName.Core) {
      return 'Admiral Core';
    } else if (agencyPlanName === AgencyPlanName.Allianz) {
      return 'Admiral Allianz';
    } else {
      return 'Sonstige';
    }
  }

  static formatAgencyStatusEndsAtStr(agency: IAgency) {
    const endsAt = Util.formatAgencyStatusEndsAt(agency.startedAt, agency.status!, agency.endsAt);

    if (
      (endsAt && agency.endsAt) ||
      agency.status === AgencyStatus.YEARLY ||
      agency.status === AgencyStatus.MONTHLY ||
      agency.status === AgencyStatus.DEMO
    ) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      return 'endet ' + Util.formatDate(endsAt!);
    }
    return '';
  }

  static formatAgencyStatusEndsAt(
    startedAt: Date,
    status: AgencyStatus,
    endsAt?: Date,
  ): Date | undefined {
    if (endsAt) {
      return endsAt;
    }

    if (status === AgencyStatus.INACTIVE || status === AgencyStatus.PERPETUAL) {
      return undefined;
    } else if (status === AgencyStatus.YEARLY) {
      return dayjs(startedAt).add(1, 'year').toDate();
    } else if (status === AgencyStatus.MONTHLY) {
      return dayjs(startedAt).endOf('quarter').toDate();
    } else if (status === AgencyStatus.DEMO) {
      return dayjs(startedAt).add(14, 'day').toDate();
    }
    return undefined;
  }

  static removeGoalFromArray(goalName: string, goals: IFivGoal[]) {
    return goals.filter((goal: IFivGoal) => goal.name !== goalName);
  }

  static yearlyRelativeValue(value: number) {
    const dayOfYear = dayjs().dayOfYear();
    const daysInThisYear = dayjs().isLeapYear() ? 366 : 365;
    const relative = dayOfYear / daysInThisYear;
    return value * relative;
  }

  static relativeValue(value: number, start: Date, end: Date) {
    const daysTotal = dayjs(end).diff(dayjs(start));
    const daysCompleted = dayjs().diff(dayjs(start));

    const relative = daysCompleted / daysTotal;
    return value * relative;
  }

  static formatDamageStatus(damageStatus: DamageStatus) {
    switch (damageStatus) {
      case DamageStatus.NEW:
        return 'NEU';
      case DamageStatus.IN_PROGRESS:
        return 'IN BEARBEITUNG';
      case DamageStatus.PENDING:
        return 'WARTEN AUF UNTERLAGEN';
      case DamageStatus.SUBMITTED:
        return 'EINGEREICHT';
      case DamageStatus.COMPLETED:
        return 'ABGESCHLOSSEN';
      case DamageStatus.DECLINED:
        return 'ABGELEHNT';
      // case DamageStatus.DELETED:
      //   return 'GELÖSCHT';
    }
  }

  static formatDamageStatusClass(damageStatus: DamageStatus) {
    switch (damageStatus) {
      case DamageStatus.NEW:
        return 'badge-light-primary';
      case DamageStatus.IN_PROGRESS:
        return 'badge-light-info';
      case DamageStatus.PENDING:
        return 'badge-light-dark';
      case DamageStatus.SUBMITTED:
        return 'badge-light-warning';
      case DamageStatus.COMPLETED:
        return 'badge-light-success';
      case DamageStatus.DECLINED:
        return 'badge-light-danger';
      case DamageStatus.DELETED:
        return 'badge-light-secondary';
    }
  }

  static isMobile() {
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      navigator.userAgent.toLowerCase(),
    );
  }

  static formatFileSize(size: number) {
    if (size < 1024) {
      return size + ' Byte';
    } else if (size < 1024 * 1024) {
      return (size / 1024).toFixed(0) + ' KB';
    } else if (size < 1024 * 1024 * 1024) {
      return (size / 1024 / 1024).toFixed(0) + ' MB';
    }
  }

  static async downloadExistingFile(file: IFile) {
    // download file from api
    const response = await FileApi.getFile(file.id);
    const blob = new Blob([response.data], { type: file.mimeType });
    const url = URL.createObjectURL(blob);
    // window.open(url, '_blank');
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    a.click();
  }
}
